import { useState, useEffect, useMemo } from 'react';
import { clsx } from 'clsx';
import { Mail, Phone, DollarSign, MessageSquare, Link2, Users, CreditCard, Shield, Plus, Trash2, X, Copy, Check } from 'lucide-react';
import { AutonomySlider } from '@/components/ui/AutonomySlider';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Tabs } from '@/components/ui/Tabs';
import { PageSpinner } from '@/components/ui/Spinner';
import { useAuthStore } from '@/stores/authStore';
import { useAutonomyStore } from '@/stores/autonomyStore';
import { useEmailStore } from '@/stores/emailStore';
import { useDashboardStore } from '@/stores/dashboardStore';
import { useUsersStore } from '@/stores/usersStore';
import { api, fromUnix } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import { toast } from '@/stores/toastStore';
import type { ApiResponse } from '@nexus/shared/types';

const settingsTabs = [
  { id: 'autonomy', label: 'Autonomy', icon: <Shield className="h-4 w-4" /> },
  { id: 'integrations', label: 'Integrations', icon: <Link2 className="h-4 w-4" /> },
  { id: 'team', label: 'Team', icon: <Users className="h-4 w-4" /> },
  { id: 'billing', label: 'Billing', icon: <CreditCard className="h-4 w-4" /> },
];

interface AutonomyConfig {
  module: string;
  label: string;
  actions: { key: string; label: string; level: 0 | 1 | 2 | 3 }[];
}

const autonomyDefaults: AutonomyConfig[] = [
  {
    module: 'email',
    label: 'Email Operations',
    actions: [
      { key: 'email_reply', label: 'Reply to emails', level: 0 },
      { key: 'email_classify', label: 'Classify emails', level: 0 },
      { key: 'email_followup', label: 'Send follow-ups', level: 0 },
    ],
  },
  {
    module: 'voice',
    label: 'Voice Operations',
    actions: [
      { key: 'voice_answer', label: 'Answer calls', level: 0 },
      { key: 'voice_escalate', label: 'Escalation decisions', level: 0 },
    ],
  },
  {
    module: 'support',
    label: 'Support',
    actions: [
      { key: 'ticket_respond', label: 'Respond to tickets', level: 0 },
      { key: 'ticket_close', label: 'Close tickets', level: 0 },
      { key: 'ticket_escalate', label: 'Escalate tickets', level: 0 },
    ],
  },
  {
    module: 'finance',
    label: 'Finance',
    actions: [
      { key: 'invoice_send', label: 'Send invoices', level: 0 },
      { key: 'invoice_followup', label: 'Payment follow-ups', level: 0 },
    ],
  },
  {
    module: 'growth',
    label: 'Growth Engine',
    actions: [
      { key: 'growth_insights', label: 'Generate insights', level: 0 },
      { key: 'growth_outreach', label: 'Outbound outreach', level: 0 },
    ],
  },
];

interface ProviderKeyDef {
  key: string;
  label: string;
  secret?: boolean;
}

interface ProviderDef {
  label: string;
  keys: ProviderKeyDef[];
}

interface IntegrationConfig {
  id: string;
  provider: string;
  configKey: string;
  configValue: string;
  isActive: number;
}

const providerIcons: Record<string, { icon: typeof Mail; color: string; description: string }> = {
  twilio: { icon: Phone, color: 'text-purple-400', description: 'Voice calls and SMS messaging' },
  stripe: { icon: DollarSign, color: 'text-emerald-400', description: 'Payment processing and invoicing' },
  whatsapp: { icon: MessageSquare, color: 'text-green-400', description: 'WhatsApp Business messaging' },
  openai: { icon: Shield, color: 'text-blue-400', description: 'AI models for automation' },
  anthropic: { icon: Shield, color: 'text-orange-400', description: 'Claude AI models' },
};

const PLAN_CONFIG: Record<string, { price: number; actions: string; features: string[] }> = {
  starter: { price: 49, actions: '1,500', features: ['5 team members', 'Basic AI', 'Email only'] },
  growth: { price: 149, actions: '12,000', features: ['15 team members', 'Full AI suite', 'All channels', 'API access'] },
  scale: { price: 399, actions: '60,000', features: ['50 team members', 'Priority support', 'Custom workflows', 'Dedicated CSM'] },
  enterprise: { price: 0, actions: 'Unlimited', features: ['Unlimited team', 'Custom SLA', 'On-premise option', 'White glove setup'] },
};

export function Settings() {
  const [activeTab, setActiveTab] = useState('autonomy');
  const { user: currentUser, tenant } = useAuthStore();
  const { rules, isLoading: autonomyLoading, error, fetch: fetchRules, upsert, clearError } = useAutonomyStore();
  const { accounts, fetchAccounts, createAccount } = useEmailStore();
  const { stats, fetchDashboard } = useDashboardStore();
  const { members: teamMembers, isLoading: teamLoading, fetchTeam, invite, updateRole, remove } = useUsersStore();

  // Integration forms
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({ email: '', provider: 'gmail' as string, persona: '' });
  const [addingEmail, setAddingEmail] = useState(false);
  const [deletingAccountId, setDeletingAccountId] = useState<string | null>(null);

  // Integration configs
  const [providers, setProviders] = useState<Record<string, ProviderDef>>({});
  const [integConfigs, setIntegConfigs] = useState<IntegrationConfig[]>([]);
  const [integLoading, setIntegLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [integForms, setIntegForms] = useState<Record<string, string>>({});

  // Team management state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'member' as 'admin' | 'member' });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ tempPassword: string; name: string } | null>(null);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<{ id: string; name: string } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);

  const isOwner = currentUser?.role === 'owner';
  const isOwnerOrAdmin = currentUser?.role === 'owner' || currentUser?.role === 'admin';

  useEffect(() => {
    fetchRules();
    fetchAccounts();
    fetchDashboard();
  }, [fetchRules, fetchAccounts, fetchDashboard]);

  // Fetch integration providers + saved configs
  useEffect(() => {
    if (activeTab === 'integrations') {
      setIntegLoading(true);
      Promise.all([
        api.get<ApiResponse<Record<string, ProviderDef>>>('/api/integrations/providers'),
        api.get<ApiResponse<IntegrationConfig[]>>('/api/integrations'),
      ])
        .then(([provRes, confRes]) => {
          setProviders(provRes.data ?? {});
          setIntegConfigs(confRes.data ?? []);
          // Pre-fill forms with saved values
          const forms: Record<string, string> = {};
          (confRes.data ?? []).forEach((c) => {
            forms[`${c.provider}:${c.configKey}`] = c.configValue;
          });
          setIntegForms(forms);
        })
        .catch(() => {})
        .finally(() => setIntegLoading(false));
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'team' && teamMembers.length === 0) {
      fetchTeam();
    }
  }, [activeTab, teamMembers.length, fetchTeam]);

  const autonomy: AutonomyConfig[] = useMemo(() => {
    return autonomyDefaults.map((mod) => ({
      ...mod,
      actions: mod.actions.map((action) => {
        const serverRule = rules.find(
          (r) => r.module === mod.module && r.actionType === action.key,
        );
        return {
          ...action,
          level: serverRule ? (serverRule.level as 0 | 1 | 2 | 3) : action.level,
        };
      }),
    }));
  }, [rules]);

  const handleAutonomyChange = (module: string, actionType: string, level: 0 | 1 | 2 | 3) => {
    upsert({ module, actionType, level });
  };

  // Integration status from email accounts
  const connectedProviders = useMemo(() => {
    const providers = new Set(accounts.filter((a) => a.isActive).map((a) => a.provider));
    return providers;
  }, [accounts]);

  const getIntegrationStatus = (key: string) => {
    if (key === 'gmail') return connectedProviders.has('gmail') ? 'connected' : 'not configured';
    if (key === 'twilio') return 'not configured';
    if (key === 'stripe') return 'not configured';
    if (key === 'whatsapp') return 'not configured';
    return 'not configured';
  };

  const currentPlan = tenant?.plan || 'starter';
  const planConfig = PLAN_CONFIG[currentPlan] || PLAN_CONFIG.starter;

  return (
    <div className="space-y-6 max-w-[1440px]">
      <div>
        <h2 className="text-xl font-bold text-surface-100">Settings</h2>
        <p className="text-sm text-surface-400 mt-0.5">Configure autonomy, integrations, and team access</p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={clearError} className="text-xs text-red-400 hover:text-red-300 cursor-pointer">Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <Tabs tabs={settingsTabs} active={activeTab} onChange={setActiveTab} />

      {/* Autonomy */}
      {activeTab === 'autonomy' && (
        <div className="space-y-6">
          {autonomyLoading && rules.length === 0 ? (
            <PageSpinner />
          ) : (
            autonomy.map((mod) => (
              <div key={mod.module} className="bg-surface-800 border border-surface-700 rounded-lg p-5">
                <h3 className="text-sm font-semibold text-surface-200 mb-4">{mod.label}</h3>
                <div className="space-y-5">
                  {mod.actions.map((action) => (
                    <AutonomySlider
                      key={action.key}
                      label={action.label}
                      value={action.level}
                      onChange={(level) => handleAutonomyChange(mod.module, action.key, level)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Integrations */}
      {activeTab === 'integrations' && (
        <div className="space-y-6">
          {integLoading ? (
            <PageSpinner />
          ) : (
            <>
              {/* Email Accounts */}
              <div className="bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
                <div className="px-5 py-4 border-b border-surface-700 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-surface-200">Email Accounts</h3>
                    <p className="text-xs text-surface-500 mt-0.5">Connect email accounts for sync, classification, and sending</p>
                  </div>
                  <Button size="sm" icon={<Plus className="h-3.5 w-3.5" />} onClick={() => setShowAddEmail(true)}>
                    Add Account
                  </Button>
                </div>
                {accounts.length === 0 ? (
                  <div className="px-5 py-8 text-center">
                    <p className="text-sm text-surface-400">No email accounts connected</p>
                    <Button size="sm" className="mt-3" onClick={() => setShowAddEmail(true)}>Connect Email</Button>
                  </div>
                ) : (
                  <div className="divide-y divide-surface-700/50">
                    {accounts.map((acc) => (
                      <div key={acc.id} className="flex items-center justify-between px-5 py-3">
                        <div className="flex items-center gap-3">
                          <Mail className="h-4 w-4 text-blue-400" />
                          <div>
                            <p className="text-sm font-medium text-surface-200">{acc.email}</p>
                            <p className="text-xs text-surface-500 capitalize">{acc.provider}{acc.persona ? ` — ${acc.persona}` : ''}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={acc.isActive ? 'success' : 'default'}>{acc.isActive ? 'Active' : 'Inactive'}</Badge>
                          <button
                            onClick={async () => {
                              setDeletingAccountId(acc.id);
                              try {
                                await api.del(`/api/email/accounts/${acc.id}`);
                                toast.success('Account removed');
                                fetchAccounts();
                              } catch (err) { toast.error('Failed to remove', (err as Error).message); }
                              finally { setDeletingAccountId(null); }
                            }}
                            disabled={deletingAccountId === acc.id}
                            className="p-1.5 rounded-lg text-surface-500 hover:text-red-400 hover:bg-surface-700 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Platform Integrations — API Keys */}
              {Object.entries(providers).map(([providerKey, providerDef]) => {
                const meta = providerIcons[providerKey];
                const savedKeys = integConfigs.filter((c) => c.provider === providerKey);
                const isConfigured = savedKeys.length > 0;
                const IconComp = meta?.icon ?? Shield;

                return (
                  <div key={providerKey} className="bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
                    <div className="px-5 py-4 border-b border-surface-700 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={clsx('p-2 bg-surface-700 rounded-lg', meta?.color ?? 'text-surface-300')}>
                          <IconComp className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-surface-200">{providerDef.label}</h3>
                          <p className="text-xs text-surface-500 mt-0.5">{meta?.description ?? 'External service'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={isConfigured ? 'success' : 'default'}>
                          {isConfigured ? 'Configured' : 'Not configured'}
                        </Badge>
                        {isConfigured && (
                          <button
                            onClick={async () => {
                              try {
                                await api.del(`/api/integrations/${providerKey}`);
                                toast.success(`${providerDef.label} disconnected`);
                                setIntegConfigs((prev) => prev.filter((c) => c.provider !== providerKey));
                                setIntegForms((prev) => {
                                  const next = { ...prev };
                                  providerDef.keys.forEach((k) => { delete next[`${providerKey}:${k.key}`]; });
                                  return next;
                                });
                              } catch (err) { toast.error('Failed to disconnect', (err as Error).message); }
                            }}
                            className="p-1.5 rounded-lg text-surface-500 hover:text-red-400 hover:bg-surface-700 transition-colors"
                            title="Remove all keys"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="px-5 py-4 space-y-3">
                      {providerDef.keys.map((keyDef) => {
                        const formKey = `${providerKey}:${keyDef.key}`;
                        const currentValue = integForms[formKey] ?? '';
                        const isSaving = savingKey === formKey;

                        return (
                          <div key={keyDef.key} className="flex items-end gap-3">
                            <div className="flex-1">
                              <label className="block text-xs font-medium text-surface-400 mb-1">{keyDef.label}</label>
                              <input
                                type={keyDef.secret ? 'password' : 'text'}
                                value={currentValue}
                                onChange={(e) => setIntegForms((prev) => ({ ...prev, [formKey]: e.target.value }))}
                                placeholder={keyDef.secret ? '••••••••' : `Enter ${keyDef.label.toLowerCase()}`}
                                className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-sm text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-transparent hover:border-surface-600 transition-colors"
                              />
                            </div>
                            <Button
                              size="sm"
                              variant="secondary"
                              loading={isSaving}
                              disabled={!currentValue.trim()}
                              onClick={async () => {
                                setSavingKey(formKey);
                                try {
                                  await api.put('/api/integrations', {
                                    provider: providerKey,
                                    configKey: keyDef.key,
                                    configValue: currentValue.trim(),
                                  });
                                  toast.success('Saved', `${providerDef.label} ${keyDef.label}`);
                                  // Refresh configs to get masked values
                                  const res = await api.get<ApiResponse<IntegrationConfig[]>>('/api/integrations');
                                  setIntegConfigs(res.data ?? []);
                                } catch (err) {
                                  toast.error('Failed to save', (err as Error).message);
                                } finally {
                                  setSavingKey(null);
                                }
                              }}
                            >
                              Save
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Add Email Account Modal */}
          <Modal open={showAddEmail} onClose={() => setShowAddEmail(false)} title="Connect Email Account">
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!emailForm.email.trim()) return;
                setAddingEmail(true);
                try {
                  await createAccount({
                    email: emailForm.email.trim(),
                    provider: emailForm.provider,
                    persona: emailForm.persona.trim() || undefined,
                  });
                  toast.success('Email account connected', emailForm.email);
                  setShowAddEmail(false);
                  setEmailForm({ email: '', provider: 'gmail', persona: '' });
                  fetchAccounts();
                } catch (err) {
                  toast.error('Failed to connect', (err as Error).message);
                } finally {
                  setAddingEmail(false);
                }
              }}
              className="space-y-4"
            >
              <Input label="Email Address" type="email" placeholder="you@company.com" value={emailForm.email} onChange={(e) => setEmailForm((f) => ({ ...f, email: e.target.value }))} required />
              <Select label="Provider" options={[{ value: 'gmail', label: 'Gmail' }, { value: 'outlook', label: 'Outlook' }, { value: 'imap', label: 'IMAP (other)' }]} value={emailForm.provider} onChange={(e) => setEmailForm((f) => ({ ...f, provider: e.target.value }))} />
              <Input label="AI Persona (optional)" placeholder="e.g. Professional, friendly tone" value={emailForm.persona} onChange={(e) => setEmailForm((f) => ({ ...f, persona: e.target.value }))} />
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" type="button" onClick={() => setShowAddEmail(false)}>Cancel</Button>
                <Button type="submit" loading={addingEmail} disabled={!emailForm.email.trim()}>Connect Account</Button>
              </div>
            </form>
          </Modal>
        </div>
      )}

      {/* Team */}
      {activeTab === 'team' && (
        <div className="space-y-6">
          <div className="bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-surface-700 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-surface-200">Team Members</h3>
                <p className="text-xs text-surface-500 mt-0.5">{teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''}</p>
              </div>
              {isOwnerOrAdmin && (
                <Button size="sm" icon={<Plus className="h-3.5 w-3.5" />} onClick={() => setShowInvite(true)}>
                  Invite Member
                </Button>
              )}
            </div>
            {teamLoading ? (
              <div className="py-12"><PageSpinner /></div>
            ) : teamMembers.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <p className="text-sm text-surface-400">No team members found</p>
                {isOwnerOrAdmin && (
                  <Button size="sm" className="mt-3" onClick={() => setShowInvite(true)}>Invite Member</Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-surface-700/50">
                {teamMembers.map((member) => {
                  const isSelf = member.id === currentUser?.id;
                  const isMemberOwner = member.role === 'owner';

                  return (
                    <div key={member.id} className="flex items-center justify-between px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-nexus-700 flex items-center justify-center text-xs font-bold text-nexus-200">
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-surface-200">
                            {member.name}
                            {isSelf && <span className="text-xs text-surface-500 ml-1.5">(you)</span>}
                          </p>
                          <p className="text-xs text-surface-500">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-surface-500">
                          {member.lastLoginAt ? formatRelative(fromUnix(member.lastLoginAt)) : 'Never logged in'}
                        </span>

                        {/* Role badge or dropdown */}
                        {isOwner && !isSelf && !isMemberOwner ? (
                          <select
                            value={member.role}
                            disabled={updatingRole === member.id}
                            onChange={async (e) => {
                              const newRole = e.target.value as 'admin' | 'member';
                              setUpdatingRole(member.id);
                              try {
                                await updateRole(member.id, newRole);
                                toast.success('Role updated', `${member.name} is now ${newRole}`);
                              } catch (err) {
                                toast.error('Failed to update role', (err as Error).message);
                              } finally {
                                setUpdatingRole(null);
                              }
                            }}
                            className="px-2 py-1 bg-surface-900 border border-surface-700 rounded-lg text-xs text-surface-200 focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-transparent hover:border-surface-600 transition-colors cursor-pointer disabled:opacity-50"
                          >
                            <option value="admin">admin</option>
                            <option value="member">member</option>
                          </select>
                        ) : (
                          <Badge
                            variant={
                              member.role === 'owner' ? 'purple' : member.role === 'admin' ? 'info' : 'default'
                            }
                          >
                            {member.role}
                          </Badge>
                        )}

                        {/* Remove button */}
                        {isOwnerOrAdmin && !isSelf && !isMemberOwner && (
                          <button
                            onClick={() => setConfirmRemove({ id: member.id, name: member.name })}
                            className="p-1.5 rounded-lg text-surface-500 hover:text-red-400 hover:bg-surface-700 transition-colors"
                            title="Remove member"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Invite Member Modal */}
          <Modal
            open={showInvite && !inviteResult}
            onClose={() => { setShowInvite(false); setInviteForm({ email: '', name: '', role: 'member' }); }}
            title="Invite Team Member"
          >
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!inviteForm.email.trim() || !inviteForm.name.trim()) return;
                setInviting(true);
                try {
                  const result = await invite({
                    email: inviteForm.email.trim(),
                    name: inviteForm.name.trim(),
                    role: inviteForm.role,
                  });
                  setInviteResult({ tempPassword: result.tempPassword, name: inviteForm.name.trim() });
                  toast.success('Member invited', inviteForm.email);
                } catch (err) {
                  toast.error('Failed to invite', (err as Error).message);
                } finally {
                  setInviting(false);
                }
              }}
              className="space-y-4"
            >
              <Input
                label="Name"
                placeholder="John Doe"
                value={inviteForm.name}
                onChange={(e) => setInviteForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
              <Input
                label="Email Address"
                type="email"
                placeholder="john@company.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                required
              />
              <Select
                label="Role"
                options={[
                  { value: 'member', label: 'Member' },
                  { value: 'admin', label: 'Admin' },
                ]}
                value={inviteForm.role}
                onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value as 'admin' | 'member' }))}
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => { setShowInvite(false); setInviteForm({ email: '', name: '', role: 'member' }); }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={inviting}
                  disabled={!inviteForm.email.trim() || !inviteForm.name.trim()}
                >
                  Send Invite
                </Button>
              </div>
            </form>
          </Modal>

          {/* Invite Success — show temp password */}
          <Modal
            open={!!inviteResult}
            onClose={() => { setInviteResult(null); setShowInvite(false); setInviteForm({ email: '', name: '', role: 'member' }); setCopiedPassword(false); }}
            title="Member Invited"
          >
            <div className="space-y-4">
              <p className="text-sm text-surface-300">
                <span className="font-medium text-surface-100">{inviteResult?.name}</span> has been added to your team.
                Share the temporary password below so they can log in.
              </p>
              <div className="bg-surface-900 border border-surface-700 rounded-lg p-3 flex items-center justify-between gap-3">
                <code className="text-sm text-nexus-400 font-mono select-all break-all">{inviteResult?.tempPassword}</code>
                <button
                  onClick={() => {
                    if (inviteResult?.tempPassword) {
                      navigator.clipboard.writeText(inviteResult.tempPassword);
                      setCopiedPassword(true);
                      setTimeout(() => setCopiedPassword(false), 2000);
                    }
                  }}
                  className="flex-shrink-0 p-1.5 rounded-lg text-surface-400 hover:text-surface-200 hover:bg-surface-700 transition-colors"
                  title="Copy password"
                >
                  {copiedPassword ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-surface-500">
                They can change this password after login. This is the only time this password will be shown.
              </p>
              <div className="flex justify-end pt-2">
                <Button
                  onClick={() => { setInviteResult(null); setShowInvite(false); setInviteForm({ email: '', name: '', role: 'member' }); setCopiedPassword(false); }}
                >
                  Done
                </Button>
              </div>
            </div>
          </Modal>

          {/* Confirm Remove Dialog */}
          <ConfirmDialog
            open={!!confirmRemove}
            onClose={() => setConfirmRemove(null)}
            onConfirm={async () => {
              if (!confirmRemove) return;
              setRemoving(true);
              try {
                await remove(confirmRemove.id);
                toast.success('Member removed', `${confirmRemove.name} has been removed from the team`);
                setConfirmRemove(null);
              } catch (err) {
                toast.error('Failed to remove', (err as Error).message);
              } finally {
                setRemoving(false);
              }
            }}
            title="Remove Team Member"
            description={`Are you sure you want to remove ${confirmRemove?.name ?? 'this member'}? Their account and all sessions will be permanently deleted.`}
            confirmLabel="Remove"
            variant="danger"
            loading={removing}
          />
        </div>
      )}

      {/* Billing */}
      {activeTab === 'billing' && (
        <div className="space-y-6">
          <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-surface-200 mb-3">Current Plan</h3>
            <div className="flex items-center gap-3 mb-4">
              <Badge variant="success" size="md">{currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} Plan</Badge>
              {planConfig.price > 0 && (
                <span className="text-surface-400 text-sm">${planConfig.price}/month</span>
              )}
            </div>
            <div>
              <p className="text-xs text-surface-500 mb-1">Action Usage This Cycle</p>
              <ProgressBar
                value={stats?.actionsUsed ?? 0}
                max={stats?.actionLimit ?? 1}
                showLabel
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Object.entries(PLAN_CONFIG).map(([key, plan]) => {
              const isCurrent = key === currentPlan;
              return (
                <div
                  key={key}
                  className={clsx(
                    'rounded-lg p-5 border',
                    isCurrent
                      ? 'bg-nexus-600/10 border-nexus-500/30'
                      : 'bg-surface-800 border-surface-700',
                  )}
                >
                  <h4 className="text-sm font-bold text-surface-200 capitalize">{key}</h4>
                  <p className="mt-2">
                    {plan.price > 0 ? (
                      <>
                        <span className="text-2xl font-bold text-surface-100">${plan.price}</span>
                        <span className="text-xs text-surface-500">/mo</span>
                      </>
                    ) : (
                      <span className="text-lg font-bold text-surface-200">Custom</span>
                    )}
                  </p>
                  <p className="text-xs text-surface-500 mt-1">{plan.actions} actions/month</p>
                  <ul className="mt-3 space-y-1.5">
                    {plan.features.map((f) => (
                      <li key={f} className="text-xs text-surface-400 flex items-start gap-1.5">
                        <span className="text-nexus-500 mt-0.5">-</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4">
                    {isCurrent ? (
                      <Badge variant="success">Current Plan</Badge>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="w-full"
                        onClick={() =>
                          plan.price === 0
                            ? toast.info('Contact us', 'Email sales@nexus.dev to discuss enterprise pricing.')
                            : toast.info('Coming soon', 'Plan upgrades will be available shortly.')
                        }
                      >
                        {plan.price === 0 ? 'Contact Sales' : 'Upgrade'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
